import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Trash2, CheckCircle2, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const inputCls = "bg-[#0F172A] border-white/[0.15] focus:border-[#E11D48] focus-visible:ring-[#E11D48]/30 text-white placeholder:text-slate-500 rounded-xl";

export default function AccountDeletion() {
  const [form, setForm] = useState({ email: "", username: "", reason: "" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const doSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const { data } = await api.post("/deletion-requests", form);
      setResult(data);
      toast.success("Deletion request submitted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <PublicLayout>
        <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center spark-fade-up">
          <span className="mx-auto flex items-center justify-center w-16 h-16 rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30">
            <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
          </span>
          <h1 className="mt-6 font-heading text-3xl font-bold text-white" data-testid="deletion-success-title">Request received</h1>
          <p className="mt-3 text-slate-400">
            We've received your deletion request for <span className="text-white">{result.email}</span>. Your
            profile is now deactivated and hidden. You have a 30-day grace period — log back in to cancel it.
            Otherwise your data is permanently purged on Day 30. Check your email for confirmation.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 rounded-xl border border-[#2A313C] bg-[#1F242D] px-5 py-3">
            <span className="text-sm text-slate-400">Reference</span>
            <span className="font-mono font-semibold text-[#E11D48]" data-testid="deletion-ref">{result.ref}</span>
            <button onClick={() => { navigator.clipboard.writeText(result.ref); toast.success("Copied"); }}
              className="text-slate-400 hover:text-white" data-testid="copy-ref">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2A313C] bg-[#1F242D] text-xs uppercase font-semibold tracking-[0.2em] text-[#E11D48]">
            <Trash2 className="w-3.5 h-3.5" /> Account Deletion
          </span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-white">Request account deletion</h1>
          <p className="mt-3 text-slate-400">
            Submit a request to permanently delete your Sparkd account and personal data.
          </p>
        </div>

        <div className="mb-6 flex gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-4">
          <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/90">
            Once submitted, your account enters an immediate 30-day deactivation grace period. If you
            do not log back in during this time, all profile data, photos, and match history will be
            permanently and automatically purged on Day 30.
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); setConfirmOpen(true); }}
          className="rounded-2xl border border-[#2A313C] bg-[#1F242D] p-6 sm:p-8 space-y-5" data-testid="deletion-form">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300">Account email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")}
              placeholder="The email on your Sparkd account" className={inputCls} data-testid="deletion-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="username" className="text-slate-300">Username or phone <span className="text-slate-500">(optional)</span></Label>
            <Input id="username" value={form.username} onChange={set("username")}
              placeholder="Helps us locate your account faster" className={inputCls} data-testid="deletion-username" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-slate-300">Reason <span className="text-slate-500">(optional)</span></Label>
            <Textarea id="reason" rows={4} value={form.reason} onChange={set("reason")}
              placeholder="Let us know why you're leaving (optional)" className={inputCls} data-testid="deletion-reason" />
          </div>
          <Button type="submit" disabled={submitting} data-testid="deletion-submit"
            className="w-full rounded-xl py-6 text-base font-semibold spark-gradient text-white hover:shadow-[0_0_24px_rgba(225,29,72,0.4)] transition-all">
            {submitting ? "Submitting..." : "Request deletion"}
          </Button>
        </form>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[#1F242D] border-[#2A313C] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm account deletion request</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This submits a request to permanently delete the Sparkd account for{" "}
              <span className="text-white">{form.email}</span>. This action cannot be undone once processed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2A313C] text-slate-300 hover:bg-white/5 hover:text-white" data-testid="deletion-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={doSubmit} data-testid="deletion-confirm"
              className="bg-gradient-to-r from-[#E11D48] to-[#F59E0B] text-white hover:opacity-90">
              Yes, submit request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PublicLayout>
  );
}
